import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MorphologyStore } from '../MorphologyStore';
import { clearWord, ConfirmationTypes, maybeConfirmAndProceed, saveMorphology } from '../morphologyUtils';
import RevealConfirmationDialogCluster from './RevealConfirmationDialogCluster';
import { IconButton } from '@mui/material';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import { HelpOutline, InfoOutlined, KeyboardBackspace, Pause, PlayArrow, Support } from '@mui/icons-material';
import StatsDialog from '../../shared/StatsDialog';
import { PuzzleNames } from '../../../../utils/types';
import { formatTime } from '../../shared/utils';

const MobileToolbar = () => {
  const {isPaused, autocheckOn, time, completed, toolbar: { combinedIsOpen } }
    = MorphologyStore.useState();

  const ref = useRef<any>(null);
  const navigate = useNavigate();

  const pauseButtonClass = completed ? 'hidden' : '';

  // If a click is outside the toolbar, it closes all menus.
  // A click within the menu is handled by togglesMenus, which closes all but the clicked one.
  useEffect(() => {
    const onClickOutside = (e: any) => {
      if (ref.current && !ref.current.contains(e.target)) {
        MorphologyStore.update(s => {
          s.toolbar.clearIsOpen = false;
          s.toolbar.revealIsOpen = false;
          s.toolbar.checkIsOpen = false;
          s.toolbar.combinedIsOpen = false;
        });
        document.removeEventListener('click', onClickOutside);
      }
    };
    if (combinedIsOpen) {
      document.addEventListener('click', onClickOutside);
    }
    return () => {
      document.removeEventListener('click', onClickOutside);
    };
  }, [combinedIsOpen]);

  return (
    <div ref={ref} className="morphologyToolbar mobile">
      <RevealConfirmationDialogCluster/>
      <StatsDialog puzzleName={PuzzleNames.Morphology} store={MorphologyStore as any} />

      <div className="backButtonContainer">
        <IconButton disableRipple title="Back" onClick={() => {
            navigate('/');
            window.scrollTo(0, 0);
            saveMorphology();
          }}>
            <KeyboardBackspace/>
        </IconButton>
      </div>

      <div className="timerContainer">
        <div className="timer">{formatTime(time)}</div>
        <IconButton className={pauseButtonClass} title="Pause" onClick={() => {
          MorphologyStore.update(s => {
            if (!s.isPaused) {
              saveMorphology();
            }
            s.isPaused = !s.isPaused;
          });
        }}>
          {isPaused ? <PlayArrow/> : <Pause/>}
        </IconButton>
      </div>

      <div className="infoAndHelpContainer">
        <IconButton disableRipple title="Stats" onClick={() => {
          MorphologyStore.update(s => {
            s.statsDialogIsOpen = true;
            s.isSoftPaused = true;
          });
        }}>
          <EqualizerIcon/>
        </IconButton>
        <div className="dropdownButton" onClick={() => {
          MorphologyStore.update(s => {
            s.instructionsAreOpen = !s.instructionsAreOpen;
            s.isSoftPaused = s.instructionsAreOpen;
            s.toolbar.combinedIsOpen = false;
          });
        }}>
          <div className="buttonLabel">
            <IconButton disableRipple disableTouchRipple>
              <HelpOutline/>
            </IconButton>
          </div>
        </div>

        <div className="dropdownButton" onClick={() => {
          MorphologyStore.update(s => {
            s.toolbar.aboutIsOpen = true;
            s.toolbar.combinedIsOpen = false;
          });
        }}>
          <div className="buttonLabel">
            <IconButton disableRipple disableTouchRipple>
              <InfoOutlined/>
            </IconButton>
          </div>
        </div>

        <div className="dropdownButton" onClick={() => {
          MorphologyStore.update(s => {
            s.toolbar.combinedIsOpen = !s.toolbar.combinedIsOpen;
          });
        }}>
          <div className="buttonLabel">
            <IconButton disableRipple disableTouchRipple>
              <Support/>
            </IconButton>
          </div>
          <ul className={`dropdownMenu revealMenu ${combinedIsOpen ? 'open' : ''}`}>
            <li onClick={clearWord}>Clear Word</li>
            <li onClick={() => {
              if (completed) return;
              MorphologyStore.update(s => { s.toolbar.confirmationDialog.clearAllIsOpen = true; });
            }}>Clear Puzzle</li>
            <li className={autocheckOn ? 'autocheckEnabled' : undefined} onClick={() => maybeConfirmAndProceed(ConfirmationTypes.Autocheck)}>
              Autocheck
            </li>
            <li className={autocheckOn ? 'disabled' : undefined} onClick={() => maybeConfirmAndProceed(ConfirmationTypes.CheckLetter)}>
              Check Square
            </li>
            <li className={autocheckOn ? 'disabled' : undefined} onClick={() => maybeConfirmAndProceed(ConfirmationTypes.CheckWord)}>
              Check Word
            </li>
            <li className={autocheckOn ? 'disabled' : undefined} onClick={() => maybeConfirmAndProceed(ConfirmationTypes.CheckPuzzle)}>
              Check Puzzle
            </li>
            <li onClick={() => maybeConfirmAndProceed(ConfirmationTypes.RevealLetter)}>
              Reveal Square
            </li>
            <li onClick={() => maybeConfirmAndProceed(ConfirmationTypes.RevealWord)}>
              Reveal Word
            </li>
            <li onClick={() => maybeConfirmAndProceed(ConfirmationTypes.RevealPuzzle)}>
              Reveal Puzzle
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MobileToolbar;